import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { CompensationType } from 'constants/loanApplication';
import { PROGRAM_FILTERS_GROUP_ID, SearchFilterConfigurationStatus } from 'constants/programs';
import useFilter from 'hooks/useFilter';

import { setFilterValue as setFilterValueAction } from 'store/filters/actions';
import {
  selectIsLockedFromPricingStatus,
  selectIsOBEngineType,
  selectOBFilterOptions,
} from 'store/loanApplications/selectors';

import { ProgramsAndPricingTestIds } from '../../ProgramsAndPricing.testIds';
import {
  getSelectedFilterValues,
  getShouldPrefillSearchFilters,
} from '../../ProgramsAndPricing.utils';

import ProgramFilterSelect, { DesiredTargetFilter } from './components';
import { RADIO_GROUP_OPTIONS } from './ProgramsFilterPanel.constants';
import {
  filtersGroupStyles,
  formControlStyles,
  formGroupStyles,
  getBaseFiltersWrapperStyles,
  getProgramsFilterPanelStyles,
  radioStyles,
} from './ProgramsFilterPanel.styles';

const ProgramsFilterPanel = ({
  filterDefs,
  isLoading,
  isDesiredTargetEnabled = false,
  isProgramReselection = false,
  searchFilterConfigurationStatus = SearchFilterConfigurationStatus.LOADING,
  onSearch,
}) => {
  const dispatch = useDispatch();

  const isSearchFilterPrefilledRef = useRef(false);

  const isOBEngineType = useSelector(selectIsOBEngineType);
  const isLocked = useSelector(selectIsLockedFromPricingStatus);
  const filterOptions = useSelector(selectOBFilterOptions);

  const { filterValues: compensationType, setFilterValue: setCompensationType } = useFilter({
    filtersGroupId: PROGRAM_FILTERS_GROUP_ID,
    filterId: 'compensationType',
    defaultValue: CompensationType.LENDER_PAID,
  });

  const onChangeCompensationType = useCallback(
    e => setCompensationType(e.target.value),
    [setCompensationType],
  );

  const programsFilterPanelStyles = useMemo(
    () => getProgramsFilterPanelStyles({ isDesiredTargetEnabled }),
    [isDesiredTargetEnabled],
  );
  const baseFiltersWrapperStyles = useMemo(
    () => getBaseFiltersWrapperStyles({ isDesiredTargetEnabled }),
    [isDesiredTargetEnabled],
  );

  useEffect(() => {
    if (searchFilterConfigurationStatus !== SearchFilterConfigurationStatus.LOADED) {
      isSearchFilterPrefilledRef.current = false;

      return;
    }

    const shouldPrefill = getShouldPrefillSearchFilters({
      isOBEngineType,
      isLocked,
      isProgramReselection,
      searchFilterConfigurationStatus,
    });

    if (!shouldPrefill || isSearchFilterPrefilledRef.current) {
      return;
    }

    isSearchFilterPrefilledRef.current = true;

    _.forEach(getSelectedFilterValues(filterOptions), (value, filterId) => {
      dispatch(
        setFilterValueAction({
          filtersGroupId: PROGRAM_FILTERS_GROUP_ID,
          filterId,
          value,
        }),
      );
    });
  }, [
    dispatch,
    isLocked,
    isOBEngineType,
    isProgramReselection,
    searchFilterConfigurationStatus,
    filterOptions,
  ]);

  return (
    <Stack
      data-testid={ProgramsAndPricingTestIds.PROGRAMS_FILTER_PANEL}
      sx={programsFilterPanelStyles}
    >
      <DesiredTargetFilter
        isHidden={!isDesiredTargetEnabled}
        isLoading={isLoading}
        onSearch={onSearch}
      />

      {!isDesiredTargetEnabled && (
        <FormGroup sx={formGroupStyles}>
          <FormControl sx={formControlStyles}>
            <FormLabel component="legend">
              <Typography variant="regular14">Compensation:</Typography>
            </FormLabel>
            <RadioGroup
              row
              value={compensationType ?? CompensationType.LENDER_PAID}
              onChange={onChangeCompensationType}
            >
              {RADIO_GROUP_OPTIONS.map(option => (
                <FormControlLabel
                  control={<Radio disableRipple sx={radioStyles} />}
                  data-testid={ProgramsAndPricingTestIds.getCompensationFilterId(option.value)}
                  disabled={isLoading}
                  key={option.value}
                  label={<Typography variant="regular14">{option.label}</Typography>}
                  value={option.value}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </FormGroup>
      )}

      <Stack data-testid={ProgramsAndPricingTestIds.BASE_FILTERS} sx={baseFiltersWrapperStyles}>
        <Stack sx={filtersGroupStyles}>
          {filterDefs.map(
            ({ filterId, label, selectOptions, width, filterType, defaultValue, isClearable }) => (
              <ProgramFilterSelect
                defaultValue={defaultValue}
                disabled={isLoading}
                filterId={filterId}
                filterType={filterType}
                filtersGroupId={PROGRAM_FILTERS_GROUP_ID}
                isClearable={isClearable}
                key={filterId}
                label={label}
                selectOptions={selectOptions}
                width={width}
              />
            ),
          )}
        </Stack>

        {isOBEngineType && (
          <Button
            className="h-auto"
            data-testid={ProgramsAndPricingTestIds.SEARCH_BUTTON}
            disabled={isLoading}
            onClick={onSearch}
          >
            Search
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

ProgramsFilterPanel.propTypes = {
  filterDefs: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onSearch: PropTypes.func.isRequired,
  isDesiredTargetEnabled: PropTypes.bool,
  isProgramReselection: PropTypes.bool,
  searchFilterConfigurationStatus: PropTypes.oneOf(Object.values(SearchFilterConfigurationStatus)),
};

export default memo(ProgramsFilterPanel);
